// ABOUTME: Forwarding a request onward while mirroring it back, with cleanup on either ending.
// ABOUTME: All three wrapping links need exactly this, so it is written once.

/** What a link forwards into, and what it hands results back to — both satisfy this structurally. */
interface Sink<T> {
  next: (value: T) => void
  error: (error: unknown) => void
  complete: () => void
}

interface Source<T> {
  subscribe: (sink: Sink<T>) => { unsubscribe: () => void }
}

/**
 * Every link here that wraps a request does the same three things: pass values through, and on both
 * endings — the answer and the failure — run its own cleanup before telling the caller. Spelled out
 * three times, jscpd counted it as a clone, and rightly: it is one concept, the same reason
 * isQueryOperation lives in operationKind.ts.
 *
 * The cleanup runs BEFORE the caller is told, deliberately. By the time a caller's complete or error
 * handler runs, the link's own bookkeeping is already done — the queue slot released, the timer
 * cleared, the slow-save count dropped — so nothing can observe the link mid-teardown. Teardown
 * would eventually run the cleanup anyway; what this ordering buys is that it is never late.
 */
export const mirrorSettling = <T>(
  source: Source<T>,
  observer: Sink<T>,
  onSettled: () => void,
): { unsubscribe: () => void } =>
  source.subscribe({
    next: (value) => observer.next(value),
    error: (error) => {
      onSettled()
      observer.error(error)
    },
    complete: () => {
      onSettled()
      observer.complete()
    },
  })
